import { useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import MailOutline from "@mui/icons-material/MailOutline";
import MonetizationOnOutlined from "@mui/icons-material/MonetizationOnOutlined";
import { Alert, Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
} from "components/Document";
import { PaymentDialog } from "components/Payment";
import {
  DocumentUploadSection,
  type SubmissionDocument,
} from "components/Submission/DocumentUploadSection";
import { toSubmissionDocumentDisplay as toDisplayDocument } from "components/Submission/helpers/documentHelpers";
import { SubmissionPayment } from "components/Submission/SubmissionPayment";
import { SubmissionPaymentDisplay } from "components/Submission/SubmissionPaymentDisplay";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  GetSubmissionSubmitAndFileQuery,
  Payee,
  SignatureType,
  SubmissionDocumentInput,
  SubmissionDocumentType,
  SubmissionStatus,
  SubmissionStepType,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import { useTenant } from "hooks/useTenant";
import { useWasteStepper } from "hooks/useWasteStepper";
import { UPDATE_SUBMISSION_MUTATION } from "queries/submissions";
import { useState } from "react";
import { formatDateLikeForDisplay } from "util/dates";

import { MarkFilingCompleteAction } from "../MarkFilingCompleteAction";
import { GET_SUBMISSION_ISSUES, GET_SUBMISSION_SUBMIT_AND_FILE_QUERY } from "../queries";
import { StepHeader } from "../StepHeader";
import { StepSubHeader } from "../StepSubHeader";
import { DocumentStepStatusBanner } from "./DocumentStepStatusBanner";

type StepData = NonNullable<GetSubmissionSubmitAndFileQuery["submission"]>;
type DocumentRow = NonNullable<StepData["documents"]>[number];

/** Map a server row back to the replace-semantics mutation input. */
function toDocumentInput(row: DocumentRow): SubmissionDocumentInput {
  return {
    documentId: row.document.id,
    type: row.type,
  };
}

/**
 * Submit & File step (document forms): the filing details and the signed
 * submission. There is deliberately no forward transition here — moving into
 * review/processing belongs to the review & sign flow — so the step's actions
 * are the staff-only "Mark as filing complete" once the submission reaches
 * processing, and a filed banner once complete.
 */
export function SubmitAndFileStep({ submissionId }: { submissionId: string; facilityId: string }) {
  const alerts = useAlerts();
  const { tenantId } = useTenant();
  const { isStaff } = useCurrentUser();
  const { submission, canEdit } = useWasteStepper();
  const { goToPreviousStep } = useStepperNavigation();

  const { data, refetch } = useQuery(GET_SUBMISSION_SUBMIT_AND_FILE_QUERY, {
    variables: { id: submissionId },
    fetchPolicy: "cache-and-network",
  });

  const [updateSubmission, { loading: savingDocument }] = useMutation(UPDATE_SUBMISSION_MUTATION, {
    // Uploading the signed copy satisfies the required-document rule; issues only
    // come from the `submission` query, so refetch them to clear the badge.
    refetchQueries: [GET_SUBMISSION_SUBMIT_AND_FILE_QUERY, GET_SUBMISSION_ISSUES],
    awaitRefetchQueries: true,
  });

  // Payments are created and edited through the shared PaymentDialog, associated
  // to this submission.
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | undefined>(undefined);
  // Submission content freezes once processing starts, but payment association is
  // operational filing work and remains editable for staff in the legacy flow.
  const canManagePayments = isStaff;

  const handleAddPayment = () => {
    setSelectedPaymentId(undefined);
    setPaymentDialogOpen(true);
  };

  const handleOpenPayment = (paymentId: string) => {
    setSelectedPaymentId(paymentId);
    setPaymentDialogOpen(true);
  };

  const closePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedPaymentId(undefined);
  };

  const [getDocumentDownloadLink] = useDocumentDownloadLink();
  const handleOpenDocument = async (document: SubmissionDocument) => {
    try {
      const { data: linkData } = await getDocumentDownloadLink({
        variables: { id: document.id, inline: true },
      });
      if (!linkData?.getDocumentDownloadLink) {
        throw new Error("No download link returned");
      }
      window.open(linkData.getDocumentDownloadLink, "_blank", "noopener");
    } catch (error) {
      alerts.error("Error opening document.", error);
    }
  };

  const stepData = data?.submission;
  const documents = stepData?.documents ?? [];
  const signedRows = documents.filter(
    (row) => row.type === SubmissionDocumentType.SignedSubmission
  );

  // Wet-ink in processing waits on a physical signature: staff mail it out, then
  // upload the signed copy that comes back.
  const isWetInkSignatureProcessing =
    isStaff &&
    submission?.signatureType === SignatureType.WetInk &&
    submission?.status === SubmissionStatus.ProcessingSubmission;

  const [signedDialogState, setSignedDialogState] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );

  // Replace semantics: send both full arrays so unrelated rows survive the save.
  const handleSignedDocumentSubmit = async (document: { id: string }) => {
    const submissionRows = documents.filter(
      (row) => row.type !== SubmissionDocumentType.Attachment
    );
    const attachmentRows = documents.filter(
      (row) => row.type === SubmissionDocumentType.Attachment
    );
    try {
      await updateSubmission({
        variables: {
          input: {
            id: submissionId,
            submissionDocuments: [
              ...submissionRows.map(toDocumentInput),
              {
                documentId: document.id,
                type: SubmissionDocumentType.SignedSubmission,
              },
            ],
            attachmentDocuments: attachmentRows.map(toDocumentInput),
          },
        },
      });
      alerts.success("Signed submission uploaded.");
    } catch (error) {
      alerts.error("Error saving the signed submission.", error);
    }
  };

  return (
    <Stack spacing={3}>
      <StepHeader
        title="Submit & File"
        description={
          isStaff
            ? "Review filing details and submit the signed submission."
            : "Encamp staff file the signed submission with the agency and update the status when it is complete."
        }
      />
      {isWetInkSignatureProcessing && (
        <Alert severity="info" icon={<MailOutline />}>
          <Typography fontWeight="bold" marginBottom={0.5}>
            Ready to mail for wet ink signature
          </Typography>
          <Typography variant="body2">
            This submission has been reviewed. Encamp staff will send the document for a wet-ink
            signature and update the status once it has been submitted to the regulatory agency.
          </Typography>
        </Alert>
      )}
      {!isWetInkSignatureProcessing && (
        <DocumentStepStatusBanner step={SubmissionStepType.SubmitAndFile} />
      )}
      <Stack spacing={1}>
        <StepSubHeader title="Filing details" />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Recipient
            </Typography>
            <Typography variant="body1">
              {stepData?.recipient ? prettyPrintEnumValue(stepData.recipient) : "—"}
            </Typography>
          </Box>
          <Box sx={{ width: 220 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Submission method
            </Typography>
            <Typography variant="body1">
              {stepData?.submissionMethod ? prettyPrintEnumValue(stepData.submissionMethod) : "—"}
            </Typography>
          </Box>
          <Box sx={{ width: 220 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Due date
            </Typography>
            <Typography variant="body1">
              {stepData?.dueDate ? formatDateLikeForDisplay(stepData.dueDate) : "—"}
            </Typography>
          </Box>
        </Box>
        {canManagePayments ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Payment(s)
              </Typography>
              <Tooltip title="Add payment">
                <IconButton
                  size="small"
                  aria-label="Add payment"
                  sx={{ color: "primary.main" }}
                  onClick={handleAddPayment}
                >
                  <MonetizationOnOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <SubmissionPayment
              submissionPayments={stepData?.payments ?? []}
              isEditMode
              onAddPayment={handleAddPayment}
              onOpenPayment={handleOpenPayment}
            />
          </Box>
        ) : (
          <SubmissionPaymentDisplay submissionPayments={stepData?.payments ?? []} />
        )}
      </Stack>
      <Stack spacing={1}>
        <StepSubHeader
          title="Signed submission"
          action={
            isWetInkSignatureProcessing && (
              <Button
                startIcon={<Add />}
                disabled={savingDocument}
                onClick={() =>
                  setSignedDialogState({ ...defaultAddEditDocumentDialogState, open: true })
                }
              >
                Add Signed Document
              </Button>
            )
          }
        />
        {signedRows.length > 0 ? (
          <DocumentUploadSection
            documents={signedRows.map(toDisplayDocument)}
            onDeleteDocument={() => {}}
            onSelectDocument={handleOpenDocument}
            canDeleteDocument={() => false}
            tenantId={tenantId ?? undefined}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {isWetInkSignatureProcessing
              ? "Upload the signed document once it comes back from the signer."
              : "The signed submission will appear here after review and signing."}
          </Typography>
        )}
      </Stack>
      <MarkFilingCompleteAction showFiledConfirmation />
      {canEdit && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button variant="text" onClick={() => goToPreviousStep(SubmissionStepType.SubmitAndFile)}>
            Back
          </Button>
        </Box>
      )}

      {signedDialogState.open && (
        <AddEditDocumentDialog
          dialogState={signedDialogState}
          setDialogState={setSignedDialogState}
          tenantId={tenantId || ""}
          showFacilityPicker={false}
          refetchQueries={[]}
          onSubmit={handleSignedDocumentSubmit}
        />
      )}

      {paymentDialogOpen && (
        <PaymentDialog
          open={paymentDialogOpen}
          onClose={closePaymentDialog}
          tenantId={tenantId ?? undefined}
          associatedSubmissionIds={[submissionId]}
          paymentId={selectedPaymentId}
          defaultPayeeType={Payee.Rcra}
          onCompleted={async () => {
            await refetch();
            closePaymentDialog();
          }}
        />
      )}
    </Stack>
  );
}
